import { createFileRoute, redirect } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Trans } from '@lingui/react/macro';
import { parkDataQueryOptions } from '@/hooks/queries/parks.ts';
import { defineLabelQuery } from '@/lib/route-meta.ts';
import { PageContainer } from '@/components/page-container.tsx';
import type { APIParkData } from '@/types/index.ts';
import { ChargePoints } from './+components/charge-points.tsx';
import { ChartsTabs } from './+components/charts/charts-tabs.tsx';
import { ExportCharges } from './+components/export-charges.tsx';
import { ParkCoupons } from './+components/park-coupons.tsx';
import { ParkManagement } from './+components/park-management.tsx';
import { ParkOverview } from './+components/park-overview.tsx';
import { StripeConnectPrompt } from './+components/stripe-connect-prompt.tsx';

function parseParkId(raw: string): number | null {
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : null;
}

export const Route = createFileRoute('/_logged/park/$parkId/')({
  component: ParkDashboard,
  staticData: {
    labelQuery: (params) =>
      defineLabelQuery(
        parkDataQueryOptions(Number(params.parkId)),
        (park) => park.label || park.name,
      ),
  },
  // Loading feedback while this loader resolves `park_data` comes from the
  // router-wide defaultPendingComponent (see createRouter in main.tsx).
  loader: async ({ context, params }) => {
    const parkId = parseParkId(params.parkId);
    if (parkId == null) throw redirect({ to: '/' });
    // The id is a route param now, so park_data is loaded here (loader pattern)
    // and read via useSuspenseQuery in the component. An unknown/forbidden park
    // (404/403) bounces back to the homepage; a transient failure is rethrown so
    // the route errorComponent can offer a retry instead of silently redirecting.
    try {
      await context.queryClient.ensureQueryData(parkDataQueryOptions(parkId));
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404 || status === 403) throw redirect({ to: '/' });
      throw error;
    }
  },
});

function ParkDashboard() {
  const { parkId: rawParkId } = Route.useParams();
  const parkId = Number(rawParkId);
  const { data: park } = useSuspenseQuery(parkDataQueryOptions(parkId));

  return <Dashboard park={park} parkId={parkId} />;
}

function Dashboard({ park, parkId }: { park: APIParkData; parkId: number }) {
  const stations = park.chargingstations;
  const paymentEnabled = stations.filter((station) => station.money_receiver != null).length;
  const location = [park.address, park.city].filter(Boolean).join(', ');

  const showStripeConnect = paymentEnabled === 0;

  return (
    <PageContainer>
      <header className='space-y-1'>
        <p className='text-muted-foreground text-xs font-medium tracking-widest uppercase'>
          <Trans>Park dashboard</Trans>
        </p>
        <h1 className='text-3xl font-semibold tracking-tight'>{park.label || park.name}</h1>
        {location ?
          <p className='text-muted-foreground text-sm'>{location}</p>
        : null}
      </header>

      {showStripeConnect ?
        <div className='flex flex-col gap-3'>
          <StripeConnectPrompt />
        </div>
      : null}

      <div className='flex flex-col gap-10'>
        <ParkOverview park={park} />
        <ChargePoints parkId={parkId} />
        {park.feature_only_solar ?
          <ParkManagement parkId={parkId} />
        : null}
        <ChartsTabs parkId={parkId} />
        <ExportCharges parkId={parkId} />
        {park.coupon_enabled ?
          <ParkCoupons parkId={parkId} />
        : null}
      </div>
    </PageContainer>
  );
}
